import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import type { DraftStart } from '../../../api/maintenanceDraftApi';

interface CreateDraftModalProps {
    show: boolean;
    /** With an active plan the user chooses where the draft starts; without one it starts from the building */
    hasActivePlan: boolean;
    creating?: boolean;
    error?: string | null;
    onConfirm: (start: DraftStart) => void;
    onCancel: () => void;
}

/** "Skapa nytt utkast": the one dialog the active-plan page and the start page open. */
export const CreateDraftModal: React.FC<CreateDraftModalProps> = ({ show, hasActivePlan, creating = false, error, onConfirm, onCancel }) => {
    const [start, setStart] = useState<DraftStart>('fromActive');

    // Every opening starts on the recommended choice
    useEffect(() => {
        if (show) setStart('fromActive');
    }, [show]);

    return (
        <Modal show={show} onHide={creating ? undefined : onCancel} centered>
            <Modal.Header closeButton={!creating}>
                <Modal.Title>Skapa nytt utkast</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {hasActivePlan ? (
                    <Form.Group>
                        <Form.Check
                            type="radio"
                            id="draft-start-from-active"
                            name="draft-start"
                            className="mb-3"
                            checked={start === 'fromActive'}
                            onChange={() => setStart('fromActive')}
                            disabled={creating}
                            label={
                                <>
                                    <div className="fw-semibold">Utgå från aktiv plan</div>
                                    <div className="small text-muted">
                                        Egna kostnader, kommentarer, datum, ändrade intervall, borttagna aktiviteter och
                                        exkluderade komponenter följer med. Det som följer mallarna hämtas från dagens mallar.
                                    </div>
                                </>
                            }
                        />
                        <Form.Check
                            type="radio"
                            id="draft-start-scratch"
                            name="draft-start"
                            checked={start === 'scratch'}
                            onChange={() => setStart('scratch')}
                            disabled={creating}
                            label={
                                <>
                                    <div className="fw-semibold">Börja från scratch</div>
                                    <div className="small text-muted">
                                        En ny plan baserad på byggnadens komponenter och befintliga mallar.
                                    </div>
                                </>
                            }
                        />
                    </Form.Group>
                ) : (
                    <p className="mb-0">
                        Är du säker på att du vill skapa ett nytt utkast för underhållsplan?
                        Detta kommer att skapa en ny plan baserat på byggnadens komponenter och befintliga mallar.
                    </p>
                )}
                {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onCancel} disabled={creating}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={() => onConfirm(hasActivePlan ? start : 'scratch')} disabled={creating}>
                    {creating && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                    Skapa utkast
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
